/**
 * RosterChartsHorizontalScroll Container Component
 *
 * Handles data fetching and business logic for displaying a horizontal scroll
 * of roster analytics charts on mobile screens.
 * @remarks
 * - Only displays on mobile/small screens
 * - Fetches roster player data using GraphQL query
 * - Manages loading, error, and empty states
 * - Provides font loading for chart rendering
 * - Determines dark mode for chart styling
 */

import React, { useMemo } from "react";

import RosterChartsHorizontalScrollView from "./RosterChartsHorizontalScrollView";

import { useRosterManagementListMyOrganizationsPlayersQuery } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useSafeFont } from "@/hooks/useSafeFont";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * RosterChartsHorizontalScroll container component that manages data fetching and state
 */
const RosterChartsHorizontalScroll = () => {
  const isSmallScreen = useMobileScreen();
  const font = useSafeFont(12);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const {
    data: rosterData,
    loading,
    error,
  } = useRosterManagementListMyOrganizationsPlayersQuery({
    variables: {
      first: 100,
    },
  });

  const players = useMemo(() => {
    return (
      rosterData?.listMyOrganizationsPlayers?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean) || []
    );
  }, [rosterData]);

  if (!isSmallScreen) {
    return null;
  }

  return (
    <RosterChartsHorizontalScrollView
      players={players}
      font={font}
      loading={loading}
      error={error}
      isDark={isDark}
    />
  );
};

export default RosterChartsHorizontalScroll;
